/* eslint-disable sonarjs/cognitive-complexity */

import { ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { ColDef, GridApi, ICellRendererParams } from 'ag-grid-community';
import { PermissionsEnum } from 'consts/permissions';
import { CREATIVE_STATUS_BADGE, REJECTION_STATUS_BADGE, REJECTED_TYPE } from 'consts/creative';
import { formatDateTimeNoUTC } from 'modules/I18N';
import Auth from 'modules/Auth';
import HeaderRenderer from 'components/common/HeaderRenderer';
import { getIsReseller, getLocaleCode } from 'store/publisher/selectors';
import { getEnvironmentId } from 'store/environment/selectors';
import StatusRenderer from 'components/common/StatusRenderer';
import ResellerCreativeStatus from 'components/common/ResellerCreativeStatus/ResellerCreativeStatus';
import { CreativeType } from 'components/common/types';
import { ConvertStatus, CreativeStatus } from 'components/common/types/Creative.types';
import ImageRenderer from 'components/common/ImageRenderer';
import VideoDurationRenderer from 'components/common/VideoDurationRenderer';
import { Store } from 'components/common/types/Store.types';

import copyableCellRenderer from 'components/common/CopyToClipboardCellRenderer/utils';
import DaysRemainingRenderer from './DaysRemainingRenderer';
import ButtonRenderer from './ButtonRenderer';

type UseCreativesTableColunDef = {
  onSelectionChanged: (event: { api: GridApi }) => void;
  onStatusUpdate: () => void;
  deleted: boolean;
};

export const useCreativesTableColumnDef = ({
  onSelectionChanged,
  onStatusUpdate,
  deleted,
}: UseCreativesTableColunDef): ColDef[] => {
  const isReseller = useSelector(getIsReseller);
  const localeCode = useSelector(getLocaleCode);
  const environmentId = useSelector(getEnvironmentId);
  const timezone = useSelector((state: Store) => state.header.timezone);
  const timezoneCode = timezone?.code;

  const updatedRenderer = ({ data }: ICellRendererParams): string | ReactElement => {
    if (!data) return '';

    if (data.updateStamp) {
      return (
        <>
          <p className="body-base text-neutral-600">{data?.updatedBy ?? ''}</p>
          <p className="body-sm">
            {formatDateTimeNoUTC(data.updateStamp, localeCode, timezoneCode || undefined)}
            {timezone?.tzAbr && ` (${timezone.tzAbr})`}
          </p>
        </>
      );
    }

    return '-';
  };

  const videoConversionStatusRenderer = ({ data, value }: ICellRendererParams): string => {
    if (!data) return '';

    if (data.type !== CreativeType.VIDEO) {
      return 'N/A';
    }

    return ConvertStatus[value as keyof typeof ConvertStatus];
  };

  const checkboxSelectionColumn = [
    {
      cellClass: 'creatives-table-checkbox-selection',
      headerName: '',
      minWidth: 70,
      maxWidth: 70,
      resizable: false,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
      checkboxSelection: true,
      headerComponent: HeaderRenderer,
      pinned: 'left' as ColDef['pinned'],
      headerComponentParams: {
        onSelectionChanged,
      },
      hide: !Auth.hasPermission(PermissionsEnum.CONTENT_REVIEW),
    },
  ];

  const statusColumn = (): ColDef[] => {
    if (!isReseller) {
      return deleted
        ? [
            {
              headerName: 'Days to restore',
              field: 'deletedAt',
              minWidth: 170,
              maxWidth: 220,
              resizable: false,
              cellRenderer: DaysRemainingRenderer,
              hide: !Auth.hasPermission(PermissionsEnum.CONTENT_REVIEW),
            },
          ]
        : [
            {
              headerName: 'Status',
              field: 'statusCode',
              minWidth: 150,
              headerTooltip: 'Status',
              cellRenderer: ({ data }: ICellRendererParams) => {
                if (data?.statusCode === CreativeStatus.REJECTED) {
                  const currentMarket = data?.market?.find(
                    ({ environment }: { environment: string }) => environment === environmentId,
                  );

                  return (
                    <StatusRenderer
                      value={currentMarket?.moderation?.[0]?.automatic ? REJECTED_TYPE.AUTO : REJECTED_TYPE.MANUAL}
                      statuses={REJECTION_STATUS_BADGE}
                    />
                  );
                }

                return <StatusRenderer value={data?.statusCode} statuses={CREATIVE_STATUS_BADGE} />;
              },
              colId: 'status',
              pinned: 'left' as ColDef['pinned'],
            },
          ];
    }
    return [
      {
        headerName: 'Status',
        field: 'statusCode',
        cellClass: '!overflow-visible [&>div]:!overflow-visible',
        minWidth: 250,
        maxWidth: 400,
        headerTooltip: 'Status',
        cellRenderer: ResellerCreativeStatus,
        pinned: 'left' as ColDef['pinned'],
        lockPinned: true,
        cellRendererParams: (data: ICellRendererParams) => {
          const currentMarket = data?.data?.market?.find(
            ({ environment }: { environment: string }) => environment === environmentId,
          );
          return {
            marketId: currentMarket?.id || '',
            portalAttachId: 'creatives-table',
            statusCode: currentMarket?.moderation[0].status,
          };
        },
        colId: 'status',
      },
    ];
  };

  const creativeDataColumns = [
    {
      headerName: 'Creative',
      field: 'thumbnail',
      minWidth: 150,
      headerTooltip: 'Creative',
      cellRenderer: ImageRenderer,
      cellRendererParams: (params: ICellRendererParams) => ({
        link: params.data?.id ? `/content-management/previewCreative/${params.data.id}` : '',
      }),
      pinned: 'left' as ColDef['pinned'],
    },
    {
      headerName: 'ID',
      field: 'externalId',
      minWidth: 160,
      headerTooltip: 'ID',
      sortable: true,
      cellRenderer: copyableCellRenderer(),
      pinned: 'left' as ColDef['pinned'],
    },
    {
      headerName: 'DSP',
      field: 'dspName',
      minWidth: 190,
      headerTooltip: 'DSP',
      sortable: true,
    },
    {
      headerName: 'Size (W x H)',
      minWidth: 180,
      headerTooltip: 'SIZE (Width X Height)',
      sortable: true,
      colId: 'size',
      cellRenderer: (params: ICellRendererParams) => {
        if (!params.data || !params.data.width || !params.data.height) return '';
        return `${params.data.width.toString()} x ${params.data.height.toString()}`;
      },
    },
    {
      headerName: 'Video duration',
      field: 'duration',
      minWidth: 150,
      headerTooltip: 'Video duration',
      cellRenderer: VideoDurationRenderer,
      sortable: true,
    },
    {
      headerName: 'Advertiser',
      field: 'advertiser',
      minWidth: 120,
      headerTooltip: 'Advertiser',
      sortable: true,
      cellRenderer: copyableCellRenderer(),
    },
    {
      headerName: 'Brand',
      field: 'brand',
      minWidth: 150,
      headerTooltip: 'Brand',
      sortable: true,
      cellRenderer: copyableCellRenderer(),
    },
    {
      headerName: 'Product category',
      field: 'productCategory',
      minWidth: 160,
      headerTooltip: 'Product category',
      sortable: true,
    },
    {
      headerName: 'DSP advertiser name',
      field: 'externalAdvertiserName',
      minWidth: 170,
      headerTooltip: 'DSP advertiser name',
      sortable: true,
      cellRenderer: copyableCellRenderer(),
    },
    {
      headerName: 'DSP advertiser domain',
      field: 'clickThroughUrl',
      minWidth: 170,
      headerTooltip: 'DSP advertiser domain',
      sortable: true,
      cellRenderer: copyableCellRenderer(),
    },
    {
      headerName: 'DSP seat ID',
      field: 'externalAgencyId',
      minWidth: 120,
      headerTooltip: 'DSP seat ID',
      sortable: true,
      cellRenderer: copyableCellRenderer(),
    },
    {
      headerName: 'Agency',
      field: 'agency',
      minWidth: 120,
      headerTooltip: 'Agency',
      sortable: false,
      hide: true,
      cellRenderer: copyableCellRenderer(),
    },
    {
      headerName: 'Specialist',
      field: 'specialist',
      minWidth: 120,
      headerTooltip: 'Specialist',
      sortable: false,
      hide: true,
      cellRenderer: copyableCellRenderer(),
    },
    {
      headerName: 'Formats',
      field: 'formatName',
      minWidth: 120,
      headerTooltip: 'Formats',
      sortable: true,
    },
    {
      headerName: 'Creative type',
      field: 'type',
      minWidth: 140,
      headerTooltip: 'Creative type',
      sortable: true,
    },
    {
      headerName: 'Video conversion',
      field: 'convertStatus',
      minWidth: 190,
      headerTooltip: 'Video conversion',
      cellRenderer: videoConversionStatusRenderer,
      hide: !Auth.hasPermission(PermissionsEnum.CONTENT_VIDEO_CONVERSION_STATUS_VIEW),
    },
    {
      headerName: 'Updated',
      field: 'updatedBy',
      minWidth: 220,
      headerTooltip: 'Updated',
      resizable: false,
      cellRenderer: updatedRenderer,
      sortable: true,
    },
    {
      headerName: '',
      cellClass: 'creatives-table-actions',
      cellRenderer: ButtonRenderer,
      cellRendererParams: {
        onStatusUpdate,
      },
      minWidth: 100,
      resizable: false,
      hide:
        !Auth.hasPermission(PermissionsEnum.ADMINISTRATION_CREATIVE_DELETE) &&
        !Auth.hasPermission(PermissionsEnum.ADMINISTRATION_CREATIVE_RESTORE),
    },
  ];

  return [...checkboxSelectionColumn, ...statusColumn(), ...creativeDataColumns];
};

/* eslint-enable sonarjs/cognitive-complexity */
